import { createSelector } from 'reselect';

import type { Connector } from '@avalara/types/index';

import { getUnitPriceForQuantity } from '../../shared/helpers/volume-pricing';
import { getOfferPlanPrice } from '../lib/offers';

import type { OfferItem, OfferOrderSummary, OfferPlanClientDetails } from '@avalara/types/offers';
import type { State } from '@avalara/types/reducer-states';

const getDiscountPercent = (state: State) => state.order.discountPercent ?? 0;
const getOrderQuantity = (state: State) => state.order.quantity;
const getVolumePricingTiers = (state: State) => state.offer.volumePricingTiers;
const getBaseOrderSummary = (state: State) => state.offer.orderSummary;
const getPlans = (state: State) => state.offer.plans;
const getSalesTax = (state: State) => state.order.salesTax ?? 0;
export const getSelectedPlanName = (state: State) => state.order.selectedPlanName;

export const getValidatedConnector = (state: State): Connector | undefined =>
  state.order.isConnectorValidated ? state.account.connector : undefined;

export const getValidatedConnectorId: (state: State) => string | undefined = createSelector(
  [getValidatedConnector],
  (validatedConnector) => validatedConnector?.id ?? undefined,
);

export const getSelectedPlan: (_: State) => OfferPlanClientDetails | null | undefined = createSelector(
  [getPlans, getSelectedPlanName],
  (plans, selectedPlanName) => plans.find((plan) => plan.name === selectedPlanName),
);

export const getProductItems: (_: State) => Array<OfferItem> = createSelector(
  [getSelectedPlan],
  (selectedPlan) => selectedPlan?.productItems || [],
);

export const getOrderSummary: (_: State) => OfferOrderSummary = createSelector(
  [getBaseOrderSummary, getSelectedPlan],
  (baseOrderSummary, selectedPlan) =>
    selectedPlan?.orderSummaryOverrides
      ? { ...baseOrderSummary, ...selectedPlan.orderSummaryOverrides }
      : baseOrderSummary,
);

export const getPrice: (_: State) => number = createSelector([getSelectedPlan], getOfferPlanPrice);

export const getPlanSpecificBillingTerms: (_: State) => Array<string> = createSelector(
  [getSelectedPlan],
  (selectedPlan) => selectedPlan?.planSpecificBillingTerms || [],
);

export const getShouldShowPreDiscountedSubtotal: (_: State) => boolean = createSelector(
  [getProductItems, getDiscountPercent],
  (productItems, discountPercent) => productItems.length > 1 && !!discountPercent,
);

/**
 * The unit price that actually applies to this order.
 *
 * For most offers this is just the selected plan's price. Offers with `volumePricingTiers` (e.g.
 * managed-returns, whose per-return rate drops as annual filing volume rises) instead resolve the
 * rate from the chosen quantity, so changing the quantity can change the unit price and not only the
 * line total.
 *
 * Falls back to the plan price when the quantity sits outside every band. In practice
 * `minQuantity`/`maxQuantity` keep the input inside the bands, so this is a guard rather than a
 * pricing decision -- an out-of-range quantity means the offer is misconfigured, and the nominal
 * plan price is a safer thing to show than zero.
 */
export const getUnitPrice: (_: State) => number = createSelector(
  [getPrice, getVolumePricingTiers, getOrderQuantity],
  (price, tiers, quantity) => getUnitPriceForQuantity(tiers, quantity) ?? price,
);

export const getOrderPreDiscountSubtotal: (_: State) => number = createSelector(
  [getUnitPrice, getOrderQuantity],
  // TODO (Andrew) - handle quantity per item rather than globally
  (unitPrice, quantity) => unitPrice * quantity,
);

export const getDiscountAmount: (_: State) => number = createSelector(
  [getOrderPreDiscountSubtotal, getDiscountPercent],
  (subtotal, discountPercent) => (subtotal * discountPercent) / 100,
);

export const getOrderSubtotal: (_: State) => number = createSelector(
  [getOrderPreDiscountSubtotal, getDiscountAmount],
  (subtotal, discount) => subtotal - discount,
);

export const getOrderTotal: (_: State) => number = createSelector(
  [getOrderSubtotal, getSalesTax],
  (subtotal, salesTax) => subtotal + salesTax,
);
